import { useCallback, useState } from "react";

import { isAPIError } from "@/core/services";
import {
  atualizarPeriodoAlocacao,
  isAlocacaoDesatualizada,
  readDivergenciasAlocacao,
  readPeriodoAlocacaoErrors,
} from "./equipe-obra.gateway";
import type {
  DivergenciasAlocacao,
  EquipeObraProfissional,
  PeriodoAlocacaoCampo,
} from "./equipe-obra.types";

export const ALOCACAO_ERROR_MESSAGE = "Não foi possível salvar as alterações. Tente novamente.";
export const ALOCACAO_FORBIDDEN_MESSAGE = "Acesso não autorizado";
export const ALOCACAO_NOT_FOUND_MESSAGE = "Alocação não encontrada.";
export const ALOCACAO_DESATUALIZADA_MESSAGE =
  "Esta alocação foi alterada por outra pessoa. A equipe será recarregada; refaça a alteração.";

export type AtualizarPeriodoAlocacaoResult =
  | { ok: true; alocacao: EquipeObraProfissional | null }
  | {
      ok: false;
      /** 403 — a tela mostra Toast "Acesso não autorizado". */
      forbidden: boolean;
      /** 404 / falha inesperada — mensagem geral. */
      message: string | null;
      /** 400 — erros por campo (`inicio` / `fim`). */
      fieldErrors: Partial<Record<PeriodoAlocacaoCampo, string>>;
      /** 409 `ALOCACAO_DESATUALIZADA` — a equipe mudou; recarregar. */
      desatualizada: boolean;
      /** 409 `DIVERGENCIAS_ALOCACAO` — conflitos/indisponibilidades apontados pelo servidor. */
      divergencias: DivergenciasAlocacao | null;
    };

function failure(patch: Partial<Extract<AtualizarPeriodoAlocacaoResult, { ok: false }>>) {
  return {
    ok: false as const,
    forbidden: false,
    message: null,
    fieldErrors: {},
    desatualizada: false,
    divergencias: null,
    ...patch,
  };
}

/** Nunca rejeita: devolve o resultado para a tela decidir onde mostrar o erro. */
export function useAtualizarPeriodoAlocacao() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const atualizar = useCallback(
    async (
      obraId: string,
      alocacaoId: string,
      periodo: { inicio: string; fim: string },
      /** `justificativa`: motivo de manter conflito/indisponibilidade; `ifMatch`: `updatedAt` lido. */
      opcoes: { justificativa?: string; ifMatch?: string } = {},
    ): Promise<AtualizarPeriodoAlocacaoResult> => {
      setIsSubmitting(true);

      try {
        const alocacao = await atualizarPeriodoAlocacao({
          obraId,
          alocacaoId,
          inicio: periodo.inicio,
          fim: periodo.fim,
          justificativa: opcoes.justificativa,
          ifMatch: opcoes.ifMatch,
        });
        return { ok: true, alocacao };
      } catch (error) {
        if (!isAPIError(error)) return failure({ message: ALOCACAO_ERROR_MESSAGE });

        if (error.status === 403) return failure({ forbidden: true });
        if (isAlocacaoDesatualizada(error)) {
          return failure({ desatualizada: true, message: ALOCACAO_DESATUALIZADA_MESSAGE });
        }
        const divergencias = error.status === 409 ? readDivergenciasAlocacao(error) : null;
        if (divergencias) return failure({ divergencias });
        if (error.status === 404) {
          return failure({ message: error.message || ALOCACAO_NOT_FOUND_MESSAGE });
        }

        if (error.status === 400) {
          const { fields, general } = readPeriodoAlocacaoErrors(error);
          if (Object.keys(fields).length > 0 || general.length > 0) {
            return failure({ fieldErrors: fields, message: general[0] ?? null });
          }
          return failure({ message: error.message || ALOCACAO_ERROR_MESSAGE });
        }

        return failure({ message: ALOCACAO_ERROR_MESSAGE });
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { atualizar, isSubmitting };
}
